import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { notificationsApi } from '../../api/notifications-api';
import { useAuth } from '../../auth/AuthContext';
import { ErrorState } from '../../components/ErrorState';
import { PageLoadingState } from '../../components/PageLoadingState';
import type { Site } from '../../domain/inventory';
import { siteLabel } from '../../lib/site-label';

/** Devices left out of the summary until a date, and why. */
export function SnoozesTab({ sitesById }: { sitesById: ReadonlyMap<string, Site> }) {
  const { canMutate } = useAuth();
  const queryClient = useQueryClient();
  const [includeExpired, setIncludeExpired] = useState(false);
  const snoozes = useQuery({
    queryKey: ['notifications', 'snoozes', includeExpired],
    queryFn: () => notificationsApi.listSnoozes(includeExpired),
  });
  const end = useMutation({
    mutationFn: (id: string) => notificationsApi.deleteSnooze(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const now = Date.now();

  return (
    <section className="panel notification-tab" aria-label="Snoozed devices">
      <div className="panel-heading">
        <div>
          <h2>Snoozed devices</h2>
          <p className="notification-subtitle">Left out of the summary until the date shown.</p>
        </div>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={includeExpired}
            onChange={(e) => setIncludeExpired(e.target.checked)}
          />
          Show ended snoozes
        </label>
      </div>
      {snoozes.isLoading ? (
        <PageLoadingState label="Loading snoozes" className="section-loading-state" />
      ) : null}
      {snoozes.isError ? (
        <ErrorState
          title="Snoozes could not be loaded"
          rpcName="ListNotificationSnoozes"
          onRetry={() => void snoozes.refetch()}
        />
      ) : null}
      {snoozes.isSuccess && snoozes.data.length === 0 ? (
        <p className="empty-note">No devices are snoozed.</p>
      ) : null}
      {snoozes.isSuccess && snoozes.data.length > 0 ? (
        <div className="summary-table notification-table">
          <table>
            <thead>
              <tr>
                <th scope="col">Device</th>
                <th scope="col">Site</th>
                <th scope="col">Until</th>
                <th scope="col">Reason</th>
                <th scope="col">By</th>
                <th scope="col">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {snoozes.data.map((s) => {
                const site = s.siteId ? sitesById.get(s.siteId) : undefined;
                const active = Date.parse(s.until) > now;
                return (
                  <tr key={s.id}>
                    <th scope="row" data-label="Device">
                      <Link to={`/inventory/${encodeURIComponent(s.itemId)}`} className="mono">
                        {s.itemId}
                      </Link>
                    </th>
                    <td data-label="Site">{site ? siteLabel(site) : (s.siteId ?? '—')}</td>
                    <td data-label="Until">
                      {s.until.slice(0, 10)}
                      {active ? null : <small className="table-subtext">Ended</small>}
                    </td>
                    <td data-label="Reason">{s.reason}</td>
                    <td data-label="By">{s.createdBy ?? ''}</td>
                    <td className="notification-actions">
                      {active ? (
                        <button
                          type="button"
                          className="link-button"
                          onClick={() => end.mutate(s.id)}
                          disabled={!canMutate || end.isPending}
                        >
                          End snooze<span className="visually-hidden"> for {s.itemId}</span>
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
